/**
 * Non-blocking leg-status lookup for order-level lists.
 *
 * Callers render the stored status immediately; chips upgrade to the
 * leg-derived rollup once this resolves. The query key is the sorted id list,
 * so several sections of one page (e.g. subscribed orders + the late tables)
 * share a single cached result.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTenant } from "@/hooks/useTenant";
import { fetchLegStatusSummaries, type LegSummaryMap } from "@/lib/order-leg-summaries";
import type { OrderLegStatusSummary } from "@/types/tms";

const EMPTY: LegSummaryMap = new Map();

export function useOrderLegSummaries(orderIds: Array<string | null | undefined> | undefined) {
  const { tenantId, tenantReady } = useTenant();
  const ids = useMemo(
    () => [...new Set((orderIds ?? []).filter(Boolean) as string[])].sort(),
    [orderIds],
  );

  const { data } = useQuery({
    queryKey: ["order-leg-summaries", tenantId, ids],
    enabled: tenantReady && !!tenantId && ids.length > 0,
    staleTime: 30_000,
    queryFn: () => fetchLegStatusSummaries(ids, tenantId),
  });

  const map = data ?? EMPTY;
  return {
    legSummaries: map,
    legsFor: (orderId: string | null | undefined): OrderLegStatusSummary[] | undefined =>
      orderId ? map.get(orderId) : undefined,
  };
}
